import { toast } from "sonner";
import { createLogger } from "@brev-io/common/src/utils/logger";

const logger = createLogger("clipboard");

interface CopyRichTextOptions {
  /** HTML representation for rich-text aware paste targets (Docs, Slack, Notion, Word). */
  html: string;
  /** Plain-text fallback for terminals, raw inputs, and unsupported browsers. */
  plainText: string;
  /** Toast text on success; null for no toast (the caller shows its own feedback). */
  successMessage?: string | null;
  /** Toast text on failure; null for no toast. */
  errorMessage?: string | null;
}

/**
 * Copy formatted content to the clipboard. Writes both `text/html` and
 * `text/plain` so rich-text consumers preserve formatting while plain-text
 * consumers still get a clean version. Falls back to `writeText` when the
 * `ClipboardItem` API isn't available (older browsers / non-secure contexts).
 *
 * NOTE: Must be called synchronously inside a user-gesture handler — Safari
 * rejects `clipboard.write` if the call is preceded by an `await`. Build the
 * `ClipboardItem` first, then await the write.
 */
export async function copyRichTextToClipboard({
  html,
  plainText,
  successMessage = "Copied to clipboard",
  errorMessage = "Failed to copy to clipboard",
}: CopyRichTextOptions): Promise<boolean> {
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
    } else {
      await navigator.clipboard.writeText(plainText);
    }
    if (successMessage) toast.success(successMessage);
    return true;
  } catch (error) {
    logger.error("Failed to copy to clipboard", error);
    // Last-ditch fallback: try plain text only. Some browsers reject
    // ClipboardItem under certain permission states but allow writeText.
    try {
      await navigator.clipboard.writeText(plainText);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (fallbackError) {
      logger.error("writeText fallback also failed", fallbackError);
      if (errorMessage) toast.error(errorMessage);
      return false;
    }
  }
}

/** Copy plain text, toasting the outcome. Resolves true when the text reached the clipboard. */
export async function copyTextToClipboard(
  text: string,
  {
    successMessage = "Copied to clipboard",
    errorMessage = "Couldn't copy. Select the text and copy it instead.",
  }: Pick<CopyRichTextOptions, "successMessage" | "errorMessage"> = {}
): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    if (successMessage) toast.success(successMessage);
    return true;
  } catch (error) {
    logger.error("Failed to copy to clipboard", error);
    if (errorMessage) toast.error(errorMessage);
    return false;
  }
}
